import { execFile } from "node:child_process";
import { mkdir, readFile, readdir, rm, writeFile } from "node:fs/promises";
import { existsSync } from "node:fs";
import path from "node:path";
import { spawnSync } from "node:child_process";
import { promisify } from "node:util";
import { Command } from "commander";
import {
  AUTOPILOT_DEFAULTS,
  CONFIG_FILE,
  LEGACY_CONFIG_FILE,
  loadConfig,
  resolveConfigPath,
  BRIDGE_TARGETS,
  buildCodeMap,
  buildFrontmatter,
  detectStacksFromManifests,
  findProjectRoot,
  memoryFilePath,
  proposeSeedsFromCommits,
  resolveHaivePaths,
  saveCodeMap,
  saveConfig,
  serializeMemory,
  type BridgeTarget,
  type GitCommit,
} from "@hivelore/core";
import { setUiJsonMode, ui } from "../utils/ui.js";
import { findDocFile } from "../utils/doc-files.js";
import { detectBridgeTargets } from "../utils/bridge-detect.js";
import { writeBridgeFiles } from "../utils/bridge-files.js";
import { setupAgentMode } from "./agent.js";
import { applyAutopilotRepairs } from "../utils/autopilot.js";
import { generateBootstrapContext } from "./init-bootstrap.js";
import {
  isValidStack,
  seedStackPack,
  SUPPORTED_STACKS,
  type StackName,
} from "./init-stack-packs.js";

const execFileAsync = promisify(execFile);

declare const __HAIVE_VERSION__: string;
const HAIVE_GITHUB_ACTION_REF = `v${__HAIVE_VERSION__}`;

const PROJECT_CONTEXT_TEMPLATE = `# Project context

> Generated by \`hivelore init\`. Run \`hivelore init --bootstrap\` to auto-fill from your codebase,
> or invoke the MCP prompt \`bootstrap_project\` in your AI client for a richer AI-generated version.
> **AI agents**: use the **hivelore** MCP (\`get_briefing\` / \`mem_relevant_to\`) before substantive edits — see bridge files and \`.cursor/rules/haive-mcp-required.mdc\`.

## Architecture
TODO — high-level overview of the codebase.

## Key modules
TODO — list main modules and their purpose.

## Conventions
TODO — coding conventions, naming, patterns the team follows.

## Glossary
TODO — domain terms and what they mean here.

## Gotchas
TODO — known traps, surprising behavior, things newcomers stub their toes on.
`;

// Bridge files are a table of contents, not a manual: a short, stable entry
// point that tells the agent where the real context lives. Kept deliberately
// under ~30 lines so it never crowds out the task in the agent's context window.
/** Cursor \`.cursor/rules/*.mdc\` — alwaysApply so agents see it even if bridge files are thin. */
const CURSOR_HAIVE_RULE_MDC = `---
description: Require Hivelore MCP (get_briefing / mem_relevant_to) before substantive repo edits
alwaysApply: true
---

# Hivelore — required for agents

This repository uses **Hivelore**. Running \`hivelore init\` means the team expects agents to **use the hivelore MCP server by default**, not skip it.

## Before you change the repo

1. Call **\`get_briefing\`** with \`task\` (and \`files\` / \`symbols\` when known), **or** \`mem_relevant_to\` if full project context is already loaded and you only need ranked memories.
2. Then read/search the codebase as needed. Do not invert this order for non-trivial work.

## During / after the task

- On failure: **\`mem_tried\`** immediately.
- Before closing a substantive session: MCP prompt **\`post_task\`** when there is something worth capturing.
- Before final response: **\`hivelore enforce finish\`** must pass; it checks commit/push, release version/tag protocol, and GitHub Actions success for pushed HEAD when the repo has a GitHub remote.

## If hivelore MCP is missing

Tell the developer to enable the **hivelore** server (e.g. \`hivelore mcp --stdio\` in client config) and restart the client. Do not silently ignore Hivelore.
`;

const CI_WORKFLOW = `name: hivelore-sync

on:
  push:
    branches: [main, master]
  pull_request:
    branches: [main, master]

jobs:
  # On push to main/master: sync anchors + auto-promote + commit changes
  sync-on-merge:
    if: github.event_name == 'push'
    runs-on: ubuntu-latest
    permissions:
      contents: write
    steps:
      - uses: actions/checkout@v4
        with:
          fetch-depth: 0

      - uses: actions/setup-node@v4
        with:
          node-version: '20'

      - name: install hivelore
        run: npm install -g @hivelore/cli

      - name: refresh memory anchors + auto-promote
        run: hivelore sync --since HEAD~1 || true

      - name: commit updated memories (if any)
        run: |
          git config user.name "github-actions[bot]"
          git config user.email "github-actions[bot]@users.noreply.github.com"
          git add .ai/
          git diff --cached --quiet || git commit -m "chore: hivelore sync [skip ci]"
          git push

  # On pull request: warn if PR touches files that would invalidate memories
  pr-stale-check:
    if: github.event_name == 'pull_request'
    runs-on: ubuntu-latest
    permissions:
      pull-requests: write
    steps:
      - uses: actions/checkout@v4
        with:
          fetch-depth: 0

      - uses: actions/setup-node@v4
        with:
          node-version: '20'

      - name: install hivelore
        run: npm install -g @hivelore/cli

      - name: verify memory anchors touched by this PR
        id: verify
        run: |
          hivelore memory verify 2>&1 | tee /tmp/haive-verify.txt || true
          STALE=$(grep -c 'stale' /tmp/haive-verify.txt || echo 0)
          echo "stale_count=$STALE" >> "$GITHUB_OUTPUT"

      - name: comment on PR if stale memories detected
        if: steps.verify.outputs.stale_count != '0'
        uses: actions/github-script@v7
        with:
          script: |
            const fs = require('fs');
            const report = fs.readFileSync('/tmp/haive-verify.txt', 'utf8').trim();
            await github.rest.issues.createComment({
              owner: context.repo.owner,
              repo: context.repo.repo,
              issue_number: context.issue.number,
              body: \`### hivelore — Stale memories detected\\n\\nSome memories anchored to code modified in this PR may be outdated:\\n\\n\\\`\\\`\\\`\\n\${report}\\n\\\`\\\`\\\`\\n\\nRun \\\`hivelore memory verify --update\\\` locally to refresh them before merging.\`
            });

  # ── Hivelore PR Memory Check ─────────────────────────────────────────────────
  # Posts a comment on every PR surfacing memories relevant to the changed files.
  # Reviewers and AI agents see gotchas, conventions, and action_required items
  # before they start coding.
  pr-memory-check:
    if: github.event_name == 'pull_request'
    runs-on: ubuntu-latest
    permissions:
      pull-requests: write
      contents: read
    steps:
      - uses: actions/checkout@v4

      - uses: Doucs91/hivelore/packages/github-action@${HAIVE_GITHUB_ACTION_REF}
        with:
          github-token: \${{ secrets.GITHUB_TOKEN }}
          # post-if-empty: 'true'   # uncomment to always post (even when no memories found)
          # max-memories: '5'       # limit memories per file in the comment

  # On pull request: fail if the harness quality score regressed vs the committed baseline.
  # Measures whether the right memories still surface and the right sensors still fire.
  # No-op (passes) when no .ai/eval/baseline.json exists — safe to keep enabled before you
  # ever create one. To turn the gate on: run \`hivelore eval --baseline\` locally and commit
  # .ai/eval/baseline.json. Needs nothing external — no secrets, no services.
  pr-eval-gate:
    if: github.event_name == 'pull_request'
    runs-on: ubuntu-latest
    steps:
      - uses: actions/checkout@v4

      - uses: actions/setup-node@v4
        with:
          node-version: '20'

      - name: install hivelore
        run: npm install -g @hivelore/cli

      - name: harness quality regression gate
        run: hivelore eval --regression-gate --record --ref "\${{ github.sha }}"

      - name: harness quality trend
        if: always()
        run: hivelore eval --trend || true

  # On push to main: push shared memories to the hub (if hubPath is configured)
  # Uncomment and configure hubPath in .ai/hivelore.config.json to enable.
  # hub-push:
  #   if: github.event_name == 'push'
  #   needs: sync-on-merge
  #   runs-on: ubuntu-latest
  #   permissions:
  #     contents: write
  #   steps:
  #     - uses: actions/checkout@v4
  #       with:
  #         fetch-depth: 0
  #     - uses: actions/setup-node@v4
  #       with:
  #         node-version: '20'
  #     - name: install hivelore
  #       run: npm install -g @hivelore/cli
  #     - name: push shared memories to hub
  #       run: hivelore hub push --commit
  #       # Requires hubPath in .ai/hivelore.config.json pointing to a cloned hub repo.
`;

export function registerInit(program: Command): void {
  program
    .command("init")
    .description(
      "Initialize a Hivelore project — autopilot mode ON by default (zero human intervention).\n" +
      "  Auto-bootstraps project-context.md from local files and seeds detected stack packs.\n" +
      "  Seeds draft memories from git revert/hotfix history (--seed, on by default).\n" +
      "  Add --manual to control memory approval and session recaps yourself.\n" +
      "  Add --no-bootstrap and --stack none to disable the auto-features.",
    )
    .option("-d, --dir <dir>", "project root", process.cwd())
    .option("--no-bridges", "do not generate any native agent bridge files")
    .option(
      "--bridge-targets <list>",
      `which agent bridges to generate: 'auto' (default — clients detected on this machine/repo, plus AGENTS.md) | 'all' | comma-list.\n` +
      `  Available: ${BRIDGE_TARGETS.join(", ")}. Each carries top memories + block sensors.`,
      "auto",
    )
    .option("--with-ci", "write a GitHub Actions workflow (.github/workflows/hivelore-sync.yml) — included automatically in autopilot mode")
    .option(
      "--manual",
      "opt out of autopilot: memories require manual approval, no auto-session recap, no auto-context",
    )
    .option(
      "--bootstrap",
      "auto-generate .ai/project-context.md from package.json, README, and directory structure (ON by default in autopilot)",
    )
    .option(
      "--no-bootstrap",
      "skip the project-context auto-generation (only the default template is written)",
    )
    .option(
      "--stack <stacks>",
      `pre-seed validated memory packs for the given stacks (comma-separated).\n` +
      `  Supported: ${SUPPORTED_STACKS.join(", ")}.\n` +
      `  Defaults to 'auto' in autopilot mode (detects from package.json/requirements.txt/go.mod/pom.xml). Pass 'none' to disable.`,
    )
    .option(
      "--seed",
      "seed draft memories from git revert/hotfix history (ON by default in autopilot)",
    )
    .option(
      "--no-seed",
      "skip git-history seeding",
    )
    .option(
      "--seed-limit <n>",
      "max git seeds to propose",
      "20",
    )
    .option(
      "--no-mcp-setup",
      "skip auto-configuring hivelore MCP (hivelore mcp --stdio) in Cursor / VS Code / Claude Code",
    )
    .option(
      "-y, --yes",
      "approve user-level AI client configuration prompts during agent setup",
      false,
    )
    .option("--json", "emit a machine-readable summary on stdout (human logs go to stderr)", false)
    .action(async (opts: {
      dir: string;
      bridges: boolean;
      bridgeTargets?: string;
      withCi?: boolean;
      manual?: boolean;
      bootstrap?: boolean;
      stack?: string;
      seed?: boolean;
      seedLimit?: string;
      mcpSetup: boolean;
      yes?: boolean;
      json?: boolean;
    }) => {
      const root = path.resolve(opts.dir);
      const paths = resolveHaivePaths(root);
      const autopilot = opts.manual !== true; // autopilot is ON by default
      const json = opts.json === true;
      if (json) setUiJsonMode(true); // keep stdout a clean JSON channel

      // In autopilot mode, default-on the value-from-day-zero auto-features
      // unless the user explicitly opted out. opts.bootstrap is `false` only
      // when the user passed --no-bootstrap; it's `undefined` when neither flag
      // was given, which we resolve to autopilot's default.
      const wantBootstrap = opts.bootstrap === undefined ? autopilot : opts.bootstrap;
      const wantStack =
        opts.stack === undefined
          ? autopilot ? "auto" : undefined
          : opts.stack === "none" ? undefined : opts.stack;
      const wantSeed = opts.seed === undefined ? autopilot : opts.seed;
      const seedLimit = Math.max(1, parseInt(opts.seedLimit ?? "20", 10) || 20);

      // Accumulate stats for the first-session report
      const report = {
        stacksLoaded: [] as string[],
        totalMemories: 0,
        totalSensors: 0,
        gitSeedsWritten: 0,
        gitCommitsScanned: 0,
        gitRevertsFound: 0,
        gitRecurring: 0,
        bridgesWritten: 0,
        bridgeTargets: [] as string[],
      };

      if (existsSync(paths.haiveDir)) {
        ui.warn(`.ai/ already exists at ${paths.haiveDir} — leaving existing files in place.`);
      }

      await mkdir(paths.personalDir, { recursive: true });
      await mkdir(paths.teamDir, { recursive: true });
      await mkdir(paths.moduleDir, { recursive: true });
      await mkdir(paths.modulesContextDir, { recursive: true });
      await ensureAiRuntimeLayout(paths.runtimeDir);
      await ensureAiCacheLayout(path.join(paths.haiveDir, ".cache"));

      // ── project-context.md ───────────────────────────────────────────────
      if (!existsSync(paths.projectContext)) {
        if (wantBootstrap) {
          ui.info("Bootstrapping project context from local files…");
          try {
            const context = await generateBootstrapContext(root);
            await writeFile(paths.projectContext, context, "utf8");
            ui.success("Created .ai/project-context.md (auto-bootstrapped from local files)");
          } catch (err) {
            ui.warn(`Bootstrap failed (${String(err)}) — writing default template instead`);
            await writeFile(paths.projectContext, PROJECT_CONTEXT_TEMPLATE, "utf8");
          }
        } else {
          await writeFile(paths.projectContext, PROJECT_CONTEXT_TEMPLATE, "utf8");
          ui.success(`Created ${path.relative(root, paths.projectContext)}`);
        }
      }

      // Write config (autopilot or default). resolveConfigPath honors a legacy haive.config.json so a
      // pre-rename repo is treated as already configured.
      const resolvedConfig = resolveConfigPath(paths);
      const configExists = existsSync(resolvedConfig);
      if (!configExists) {
        await saveConfig(paths, autopilot ? AUTOPILOT_DEFAULTS : { autopilot: false });
        ui.success(
          `Created .ai/${CONFIG_FILE} (mode: ${autopilot ? "autopilot" : "standard"})`,
        );
      } else if (!resolvedConfig.endsWith(CONFIG_FILE)) {
        // Pre-rename repo: migrate the legacy file to the current name (saveConfig deletes the legacy).
        await saveConfig(paths, await loadConfig(paths));
        ui.info(`Migrated .ai/${LEGACY_CONFIG_FILE} → .ai/${CONFIG_FILE}.`);
      }

      // Native agent bridges are generated AFTER seeding (below) so they carry the
      // seeded memories + sensors, not an empty template.

      // ── Stack memory packs ───────────────────────────────────────────────
      const stacksToSeed = await resolveStacksToSeed(root, wantStack);
      if (stacksToSeed.length > 0) {
        let totalSeeded = 0;
        let totalSensors = 0;
        for (const stack of stacksToSeed) {
          const result = await seedStackPack(paths, stack);
          if (result.memories > 0) {
            ui.success(`Seeded ${result.memories} memories for stack: ${stack} (${result.sensors} sensors)`);
            totalSeeded += result.memories;
            totalSensors += result.sensors;
            report.stacksLoaded.push(stack);
          } else {
            ui.info(`Stack pack '${stack}': all memories already exist — skipped`);
          }
        }
        report.totalMemories += totalSeeded;
        report.totalSensors += totalSensors;
        if (totalSeeded > 0) {
          ui.success(
            `${totalSeeded} starter memories seeded (generic stack guidance, kept at background priority)`,
          );
          ui.info(
            "Anchor them to real files or replace them with repo-specific notes to make them high-signal.",
          );
        }
      }

      // ── Git-history seeding (cold-start) ────────────────────────────────
      if (wantSeed) {
        const gitStats = await seedFromGitHistory(root, paths, seedLimit);
        report.gitCommitsScanned = gitStats.scanned;
        report.gitRevertsFound = gitStats.found;
        report.gitRecurring = gitStats.recurring;
        report.gitSeedsWritten = gitStats.written;
        report.totalMemories += gitStats.written;
        if (gitStats.found > 0) {
          ui.success(
            `Git seeding: ${gitStats.found} revert/hotfix signal(s) found → ${gitStats.written} draft(s) written` +
            (gitStats.recurring > 0 ? ` (${gitStats.recurring} recurring theme(s))` : ""),
          );
        } else {
          ui.info("Git seeding: no revert/hotfix signals found — run `hivelore memory seed-git` later.");
        }
      }

      // ── Native agent bridges (reach) ─────────────────────────────────────
      // Generated here, after seeding, so each bridge carries the freshly-seeded
      // memories + block sensors — not an empty template. Idempotent: existing
      // files keep their manual content, only the hivelore marker blocks refresh.
      if (opts.bridges) {
        const targets = resolveBridgeTargets(opts.bridgeTargets, root);
        const res = await writeBridgeFiles(root, paths, { targets });
        // The "always use the MCP" Cursor nudge is complementary to the memories bridge.
        if (targets.includes("cursor")) {
          await writeCursorHaiveRule(root);
        }
        const made = res.created.length + res.updated.length;
        report.bridgesWritten = made;
        report.bridgeTargets = targets;
        if (res.created.length > 0) {
          ui.success(`Generated ${res.created.length} agent bridge(s): ${res.created.join(", ")}`);
        }
        if (res.updated.length > 0) {
          ui.info(`Refreshed ${res.updated.length} existing bridge(s): ${res.updated.join(", ")}`);
        }
        if (made === 0) {
          ui.info("Bridges already up to date.");
        }
      }

      // autopilot: CI + enforcement hooks + code-map included automatically
      const wantCi = opts.withCi || autopilot;
      if (wantCi) {
        const workflowsDir = path.join(root, ".github", "workflows");
        const ciPath = path.join(workflowsDir, "hivelore-sync.yml");
        const legacyCiPath = path.join(workflowsDir, "haive-sync.yml");
        if (existsSync(ciPath)) {
          ui.info("CI workflow already exists — skipped");
        } else {
          await mkdir(workflowsDir, { recursive: true });
          await writeFile(ciPath, CI_WORKFLOW, "utf8");
          ui.success(`Created ${path.relative(root, ciPath)}`);
          // Migrate: remove a pre-rename haive-sync.yml so the sync workflow does not run twice.
          if (existsSync(legacyCiPath)) {
            await rm(legacyCiPath, { force: true });
            ui.info(`Migrated ${path.relative(root, legacyCiPath)} → ${path.relative(root, ciPath)}.`);
          }
        }
      }

      if (autopilot) {
        // Install agent-agnostic enforcement gates (MCP policy config, git hooks,
        // CI workflow, and supported client hooks such as Claude Code).
        const haiveBin = process.argv[1]!;
        const enforcementResult = spawnSync(
          process.execPath,
          [haiveBin, "enforce", "install", "--dir", root],
          { encoding: "utf8" },
        );
        if (enforcementResult.status === 0) {
          ui.success("Hivelore enforcement installed (MCP, git, CI, client hooks where available)");
        } else {
          ui.warn("Hivelore enforcement not fully installed — run `hivelore enforce install` manually");
        }

        // Build initial code-map
        try {
          ui.info("Building code-map…");
          const map = await buildCodeMap(root, { includeUntracked: true });
          await saveCodeMap(paths, map);
          ui.success(`Code-map built (${Object.keys(map.files).length} files)`);
        } catch {
          ui.warn("Code-map build failed — run `hivelore index code` manually");
        }

        const repairs = await applyAutopilotRepairs(root, paths, {
          applyContext: true,
          applyCorpus: true,
          applyCodeMap: false,
          applyCodeSearch: true,
        });
        for (const repair of repairs) {
          ui.info(repair.message);
        }
      }

      // ── Auto-configure MCP in AI clients ────────────────────────────────
      if (opts.mcpSetup !== false) {
        const agentSetup = await setupAgentMode(root, {
          yes: opts.yes === true,
          global: true,
          interactive: process.stdin.isTTY,
        });
        for (const r of agentSetup.project_results) {
          if (r.status === "configured" && r.path) ui.success(`hivelore MCP project config written (${path.relative(root, r.path)})`);
          else if (r.status === "error") ui.warn(`${r.client}: ${r.error}`);
        }
        for (const r of agentSetup.global_results) {
          if (r.status === "configured") ui.success(`hivelore MCP configured in ${r.client} user-level config${r.path ? ` (${r.path})` : ""}`);
          else if (r.status === "already_configured") ui.info(`hivelore MCP already present in ${r.client} user-level config — left unchanged (this project's config was written above)`);
        }
        if (agentSetup.global_skipped_reason) ui.warn(agentSetup.global_skipped_reason);
        ui.info(`Recommended agent mode: ${agentSetup.detection.recommended_mode}`);
        ui.info(agentSetup.detection.recommended_command);
        await ensureGitignoreEntries(root, [
          ".cursor/mcp.json",
          ".vscode/mcp.json",
          ".mcp.json",
        ]);
        ui.info(ui.dim("  → Restart your AI client for MCP changes to take effect."));
      }

      await ensureGitignoreEntries(root, [
        ".ai/.cache/*",
        "!.ai/.cache/.gitignore",
        ".ai/.runtime/*",
        "!.ai/.runtime/.gitignore",
        "!.ai/.runtime/README.md",
        // Machine-local tool-usage telemetry — like .cache/.runtime, it changes on every invocation
        // and is never team truth (eval baselines exclude local usage counters). Committing it made the
        // working tree perpetually dirty and forced a stash before every release.
        ".ai/.usage/",
      ]);

      ui.success(`Hivelore initialized at ${root}${autopilot ? " (autopilot mode)" : ""}`);

      if (json) {
        console.log(JSON.stringify({
          root,
          mode: autopilot ? "autopilot" : "manual",
          stacks_loaded: report.stacksLoaded,
          memories_seeded: report.totalMemories,
          sensors_active: report.totalSensors,
          git_commits_scanned: report.gitCommitsScanned,
          git_signals_found: report.gitRevertsFound,
          git_seeds_written: report.gitSeedsWritten,
          git_recurring: report.gitRecurring,
          bridges: opts.bridges !== false,
          bridges_written: report.bridgesWritten,
          ci: opts.withCi || autopilot,
        }, null, 2));
        return;
      }

      console.log();

      // ── First-session report ─────────────────────────────────────────────
      printInitReport(report);
      console.log();

      if (autopilot) {
        console.log(ui.bold("Autopilot mode is ON — Hivelore runs itself:"));
        console.log(ui.dim("  ✓ Memories go directly to validated (no approval needed)"));
        console.log(ui.dim("  ✓ Proposed memories auto-approve after 72h without rejection"));
        console.log(ui.dim("  ✓ Session recap saved automatically when the AI session closes"));
        console.log(ui.dim("  ✓ Code-map refreshes automatically after every pull"));
        console.log(ui.dim("  ✓ Agent-agnostic enforcement gates installed (MCP, git, CI, wrapper-ready)"));
        console.log(ui.dim("  ✓ CI workflows created (sync + enforcement)"));
        if (stacksToSeed.length > 0) {
          console.log(ui.dim(`  ✓ Stack memory packs pre-seeded (${stacksToSeed.join(", ")})`));
        }
        console.log();

        if (!wantBootstrap) {
          console.log(ui.bold("One remaining step (optional but recommended):"));
          console.log("  " + ui.bold("hivelore init --bootstrap") + ui.dim("  ← fill project-context.md without AI"));
          console.log("  " + ui.dim("Or in your AI client: invoke the MCP prompt ") + ui.bold("bootstrap_project"));
        } else {
          console.log(ui.bold("Project context bootstrapped from local files."));
          console.log(ui.dim("  Review .ai/project-context.md and fill in the TODO sections."));
          console.log(ui.dim("  Or invoke the MCP prompt `bootstrap_project` for a richer AI-generated version."));
        }
        const changelogFile = findDocFile(root, "changelog");
        const readmeFile = findDocFile(root, "readme");
        if (changelogFile || readmeFile) {
          console.log();
          console.log(ui.dim("  Seed more memories:"));
          if (changelogFile) {
            console.log(ui.dim(`    hivelore memory import --from ${changelogFile} --changelog   — extract breaking-change gotchas (no AI needed)`));
          }
          if (readmeFile) {
            console.log(ui.dim(`    hivelore memory import --from ${readmeFile}   — prepare an import prompt for your AI client`));
          }
        }
      } else {
        console.log(ui.bold("Next steps:"));
        if (!wantBootstrap) {
          console.log(ui.dim("  1. Fill project context (pick one):"));
          console.log("     " + ui.bold("hivelore init --bootstrap") + ui.dim("  ← instant, no AI needed"));
          console.log("     or invoke the MCP prompt " + ui.bold("bootstrap_project") + ui.dim(" in your AI client"));
        } else {
          console.log(ui.dim("  1. Review .ai/project-context.md and fill in the TODO sections."));
        }
        console.log();
        console.log(ui.dim("  2. Start every AI session with:"));
        console.log("     " + ui.bold("get_briefing({ task: '…what you are about to do…' })"));
        console.log();
        console.log(ui.dim("  Tip: run `hivelore init` (without --manual) for zero-friction autopilot mode."));
      }
    });
}

async function resolveStacksToSeed(
  root: string,
  stackOpt: string | undefined,
): Promise<StackName[]> {
  if (!stackOpt) return [];
  if (stackOpt === "auto") {
    return autoDetectStacksFromRoot(root);
  }
  // Comma-separated stack names
  return stackOpt
    .split(",")
    .map((s) => s.trim().toLowerCase())
    .filter(isValidStack);
}

/**
 * Merge dependencies from package.json files in immediate sub-packages (depth ≤ 2), skipping
 * node_modules/build/hidden dirs. Lets stack detection see frameworks that live in monorepo
 * sub-packages or nested repos rather than the root manifest. Best-effort; returns undefined if none.
 */
async function collectNestedPackageDeps(root: string): Promise<Record<string, string> | undefined> {
  const SKIP = new Set([
    "node_modules", "dist", "build", "out", ".git", ".next", "coverage", "vendor",
    // Fixture/demo trees describe what the project TESTS AGAINST, not what it is built with.
    // Vite's playground/ pulls react+vue+tailwind+express into detection and seeds four
    // irrelevant stack packs on a build-tool repo.
    "playground", "playgrounds", "example", "examples", "fixtures", "__fixtures__",
    "e2e", "test", "tests", "demo", "demos", "template", "templates", "bench", "benchmarks", "samples", "sandbox",
    // Doc sites are built with their own framework (VitePress→vue, Docusaurus→react) — that is
    // the DOCS' stack, not the product's.
    "docs", "doc", "website",
  ]);
  // Scaffolding dirs come in prefixed families (create-vite ships template-vue, template-react, …).
  const SKIP_PREFIX = /^(template|example|fixture|demo|sample)s?-/;
  const merged: Record<string, string> = {};
  let found = false;
  async function scan(dir: string, depth: number): Promise<void> {
    if (depth > 2) return;
    let entries;
    try { entries = await readdir(dir, { withFileTypes: true }); } catch { return; }
    for (const entry of entries) {
      if (!entry.isDirectory() || entry.name.startsWith(".") || SKIP.has(entry.name) || SKIP_PREFIX.test(entry.name)) continue;
      const sub = path.join(dir, entry.name);
      const pkgPath = path.join(sub, "package.json");
      if (existsSync(pkgPath)) {
        try {
          const pkg = JSON.parse(await readFile(pkgPath, "utf8")) as {
            dependencies?: Record<string, string>;
            devDependencies?: Record<string, string>;
          };
          Object.assign(merged, pkg.dependencies ?? {}, pkg.devDependencies ?? {});
          found = true;
        } catch { /* ignore */ }
      }
      await scan(sub, depth + 1);
    }
  }
  await scan(root, 0);
  return found ? merged : undefined;
}

/** Read all manifest files at root and return detected valid stacks. */
async function autoDetectStacksFromRoot(root: string): Promise<StackName[]> {
  let packageJsonDeps: Record<string, string> | undefined;
  let requirementsTxt: string | undefined;
  let goMod: string | undefined;
  let pomXml: string | undefined;

  const pkgPath = path.join(root, "package.json");
  if (existsSync(pkgPath)) {
    try {
      const pkg = JSON.parse(await readFile(pkgPath, "utf8")) as {
        dependencies?: Record<string, string>;
        devDependencies?: Record<string, string>;
      };
      packageJsonDeps = { ...(pkg.dependencies ?? {}), ...(pkg.devDependencies ?? {}) };
    } catch { /* ignore parse errors */ }
  }

  // Monorepos keep the real frameworks in sub-packages / nested repos (e.g. a Next.js frontend and a
  // NestJS backend), not the root package.json. Merge nested deps so stack detection isn't blind to
  // them — on a real marketplace monorepo the root-only scan saw "react" but missed next + nest.
  const nestedDeps = await collectNestedPackageDeps(root);
  if (nestedDeps) packageJsonDeps = { ...(packageJsonDeps ?? {}), ...nestedDeps };

  for (const name of ["requirements.txt", "requirements/base.txt", "requirements/prod.txt"]) {
    const reqPath = path.join(root, name);
    if (existsSync(reqPath)) {
      try { requirementsTxt = await readFile(reqPath, "utf8"); break; } catch { /* ignore */ }
    }
  }

  const goModPath = path.join(root, "go.mod");
  if (existsSync(goModPath)) {
    try { goMod = await readFile(goModPath, "utf8"); } catch { /* ignore */ }
  }

  const pomPath = path.join(root, "pom.xml");
  if (existsSync(pomPath)) {
    try { pomXml = await readFile(pomPath, "utf8"); } catch { /* ignore */ }
  }

  let composerJson: string | undefined;
  const composerPath = path.join(root, "composer.json");
  if (existsSync(composerPath)) {
    try { composerJson = await readFile(composerPath, "utf8"); } catch { /* ignore */ }
  }

  let gemfile: string | undefined;
  const gemfilePath = path.join(root, "Gemfile");
  if (existsSync(gemfilePath)) {
    try { gemfile = await readFile(gemfilePath, "utf8"); } catch { /* ignore */ }
  }

  // .NET / Docker / monorepo are detected by file presence (no content needed).
  const hasDockerfile = existsSync(path.join(root, "Dockerfile"));
  const hasTurboJson = existsSync(path.join(root, "turbo.json"));
  const hasNxJson = existsSync(path.join(root, "nx.json"));
  let hasCsproj = false;
  try {
    const entries = await readdir(root);
    hasCsproj = entries.some((e) => e.endsWith(".csproj") || e.endsWith(".sln"));
  } catch { /* ignore */ }

  const detected = detectStacksFromManifests({
    packageJsonDeps,
    requirementsTxt,
    goMod,
    pomXml,
    composerJson,
    gemfile,
    hasCsproj,
    hasDockerfile,
    hasTurboJson,
    hasNxJson,
  });
  return detected.filter((s): s is StackName => isValidStack(s));
}

interface GitSeedStats {
  scanned: number;
  found: number;
  recurring: number;
  written: number;
}

/** Seed draft memories from git revert/hotfix history. Returns stats. */
async function seedFromGitHistory(
  root: string,
  paths: ReturnType<typeof resolveHaivePaths>,
  limit: number,
): Promise<GitSeedStats> {
  const commits = await readGitCommits(root);
  if (commits.length === 0) return { scanned: 0, found: 0, recurring: 0, written: 0 };

  const proposals = proposeSeedsFromCommits(commits, limit);

  // Detect recurring: same slug appearing in multiple distinct commits (re-run proposals shows them)
  // proposeSeedsFromCommits already dedupes by slug, so "recurring" = proposals whose slug
  // appears in more than one source commit (we re-scan to count)
  const slugCounts = new Map<string, number>();
  for (const c of commits) {
    const sub = c.subject.trim();
    const revertMatch = sub.match(/^Revert\s+"(.+)"\s*$/i);
    const slug = revertMatch
      ? revertMatch[1]!.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 60)
      : null;
    if (slug) slugCounts.set(slug, (slugCounts.get(slug) ?? 0) + 1);
  }
  const recurring = proposals.filter((p) => (slugCounts.get(p.slug) ?? 0) > 1).length;

  let written = 0;
  for (const p of proposals) {
    const fm = {
      ...buildFrontmatter({
        type: "attempt",
        slug: p.slug,
        scope: "team",
        tags: ["seed", "git-history", p.kind],
        paths: p.paths,
      }),
      status: "draft" as const,
    };
    const body = `# ${p.what}\n\n**Why it failed / do NOT use:** ${p.why_failed}\n\n_Seeded from git ${p.kind} commit ${p.source_sha}. Review and validate (or delete) — not yet authoritative._\n`;
    const file = memoryFilePath(paths, fm.scope, fm.id, fm.module);
    if (existsSync(file)) continue; // idempotent
    await mkdir(path.dirname(file), { recursive: true });
    await writeFile(file, serializeMemory({ frontmatter: fm, body }), "utf8");
    written++;
  }

  return { scanned: commits.length, found: proposals.length, recurring, written };
}

/** Read recent git commits with touched files. Returns [] off-git or on error. */
async function readGitCommits(root: string): Promise<GitCommit[]> {
  try {
    const { stdout } = await execFileAsync(
      "git",
      ["log", "--since=365.days.ago", "--name-only", "--pretty=format:%x1f%h%x1f%s", "-n", "500"],
      { cwd: root, maxBuffer: 8 * 1024 * 1024 },
    );
    const blocks = stdout.split("\x1f").filter((b) => b.length > 0);
    const commits: GitCommit[] = [];
    for (let i = 0; i + 1 < blocks.length; i += 2) {
      const sha = blocks[i]!.trim();
      const tail = blocks[i + 1]!;
      const lines = tail.split("\n").map((l) => l.trim()).filter(Boolean);
      const subject = lines.shift() ?? "";
      commits.push({ sha, subject, files: lines });
    }
    return commits;
  } catch {
    return [];
  }
}

interface InitReport {
  stacksLoaded: string[];
  totalMemories: number;
  totalSensors: number;
  gitSeedsWritten: number;
  gitCommitsScanned: number;
  gitRevertsFound: number;
  gitRecurring: number;
  bridgesWritten: number;
  bridgeTargets: string[];
}

function printInitReport(r: InitReport): void {
  const lines: string[] = [];

  if (r.gitRevertsFound > 0) {
    const recurring = r.gitRecurring > 0 ? ` (${r.gitRecurring} recurring)` : "";
    lines.push(`  Git signals : ${r.gitCommitsScanned} commits → ${r.gitRevertsFound} revert/hotfix${recurring}`);
    lines.push(`  Drafts ready: ${r.gitSeedsWritten} draft lesson(s) from real past mistakes`);
  }
  if (r.stacksLoaded.length > 0) {
    lines.push(`  Stack packs : ${r.stacksLoaded.join(", ")} — ${r.totalSensors} sensor(s) active`);
  }
  if (r.totalMemories > 0) {
    lines.push(`  Total ready : ${r.totalMemories} lesson(s), ${r.totalSensors} sensor(s) — 0 written by hand`);
  }
  if (r.bridgesWritten > 0) {
    lines.push(`  Reach       : ${r.bridgesWritten} agent bridge(s) generated (${r.bridgeTargets.join(", ")})`);
  }

  if (lines.length === 0) return;

  const width = Math.max(...lines.map((l) => l.length), 44);
  const bar = "─".repeat(width + 2);
  console.log(ui.bold(`┌${bar}┐`));
  console.log(ui.bold(`│`) + `  Hivelore — first-session report`.padEnd(width + 1) + ui.bold(`│`));
  console.log(ui.bold(`├${bar}┤`));
  for (const line of lines) {
    console.log(ui.bold(`│`) + line.padEnd(width + 2) + ui.bold(`│`));
  }
  console.log(ui.bold(`└${bar}┘`));
  if (r.gitSeedsWritten > 0) {
    console.log(ui.dim("  Review draft seeds: hivelore memory pending   (validate or delete each one)"));
  }
  console.log(
    ui.dim("  Reach: bridges auto-refresh on `hivelore sync`; regenerate any time with `hivelore bridges sync --all`."),
  );
}

async function writeCursorHaiveRule(root: string): Promise<void> {
  const relPath = ".cursor/rules/haive-mcp-required.mdc";
  const target = path.join(root, relPath);
  if (existsSync(target)) {
    ui.info(`Cursor rule ${relPath} already exists — skipped`);
    return;
  }
  await mkdir(path.dirname(target), { recursive: true });
  await writeFile(target, CURSOR_HAIVE_RULE_MDC, "utf8");
  ui.success(`Created Cursor rule ${relPath}`);
}

/** Resolve the `--bridge-targets` option ('auto' | 'all' | comma-list) into concrete targets. */
function resolveBridgeTargets(opt: string | undefined, root: string): BridgeTarget[] {
  const raw = (opt ?? "auto").trim().toLowerCase();
  if (raw === "all") return [...BRIDGE_TARGETS];
  if (raw === "" || raw === "auto") {
    const detection = detectBridgeTargets(root);
    const named = detection.targets
      .map((t) => (detection.reasons[t] === "repo file" ? `${t} (already in repo)` : t))
      .join(", ");
    ui.info(`Bridge targets (detected): ${named} — pass --bridge-targets all for every supported client`);
    return detection.targets;
  }
  const requested = raw.split(",").map((t) => t.trim()).filter(Boolean);
  const valid = requested.filter((t): t is BridgeTarget => (BRIDGE_TARGETS as string[]).includes(t));
  const invalid = requested.filter((t) => !(BRIDGE_TARGETS as string[]).includes(t));
  if (invalid.length > 0) {
    ui.warn(`Ignoring unknown bridge target(s): ${invalid.join(", ")}. Valid: ${BRIDGE_TARGETS.join(", ")}`);
  }
  return valid.length > 0 ? valid : [...BRIDGE_TARGETS];
}

const RUNTIME_README_BODY = `# .ai/.runtime — disposable local layer

Not team truth. Use for machine-local session notes or tooling scratch files.
Official memories belong in .ai/memories/ (versioned in Git).
Only .gitignore and this README are meant to commit; everything else stays untracked.
`;

const RUNTIME_GITIGNORE_BODY = `*
!.gitignore
!README.md
`;

async function ensureAiRuntimeLayout(runtimeDir: string): Promise<void> {
  await mkdir(runtimeDir, { recursive: true });
  const gi = path.join(runtimeDir, ".gitignore");
  if (!existsSync(gi)) {
    await writeFile(gi, RUNTIME_GITIGNORE_BODY, "utf8");
  }
  const readme = path.join(runtimeDir, "README.md");
  if (!existsSync(readme)) {
    await writeFile(readme, RUNTIME_README_BODY, "utf8");
  }
}

async function ensureAiCacheLayout(cacheDir: string): Promise<void> {
  await mkdir(cacheDir, { recursive: true });
  const gi = path.join(cacheDir, ".gitignore");
  if (!existsSync(gi)) {
    await writeFile(gi, "*\n!.gitignore\n", "utf8");
  }
}

/**
 * Append gitignore entries if they aren't already present.
 * Creates .gitignore if it doesn't exist.
 * Silently no-ops on any filesystem error (non-blocking).
 */
async function ensureGitignoreEntries(root: string, patterns: string[]): Promise<void> {
  try {
    const gitignorePath = path.join(root, ".gitignore");
    let existing = "";
    if (existsSync(gitignorePath)) {
      existing = await readFile(gitignorePath, "utf8");
    }
    const lines = existing.split("\n");
    const missing = patterns.filter((p) => !lines.some((l) => l.trim() === p));
    if (missing.length === 0) return;
    const toAppend = (existing.endsWith("\n") || existing === "" ? "" : "\n") +
      "# Hivelore project-level MCP configs (machine-specific absolute paths)\n" +
      missing.join("\n") + "\n";
    await writeFile(gitignorePath, existing + toAppend, "utf8");
  } catch {
    // non-fatal
  }
}
